import { useState, useEffect, useCallback } from 'react';
import { socialMediaApi } from '../api';

export type BrandSettingsData = Awaited<
  ReturnType<typeof socialMediaApi.getBrandSettings>
>['settings'];

export interface UseBrandSettingsResult {
  settings: BrandSettingsData | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  loadSettings: () => Promise<void>;
  saveSettings: (data: Parameters<typeof socialMediaApi.upsertBrandSettings>[1]) => Promise<void>;
}

/**
 * Holder-scoped brand settings. Stays in a no-op loading state when
 * no Holder is selected so the caller doesn't have to gate the render
 * tree separately.
 */
export function useBrandSettings(holderId: string | null): UseBrandSettingsResult {
  const [settings, setSettings] = useState<BrandSettingsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    if (!holderId) {
      setSettings(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await socialMediaApi.getBrandSettings(holderId);
      setSettings(result.settings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load brand settings');
    } finally {
      setIsLoading(false);
    }
  }, [holderId]);

  const saveSettings = useCallback(
    async (data: Parameters<typeof socialMediaApi.upsertBrandSettings>[1]) => {
      if (!holderId) {
        throw new Error('No Holder selected — cannot save brand settings');
      }
      setIsSaving(true);
      setError(null);
      try {
        const result = await socialMediaApi.upsertBrandSettings(holderId, data);
        setSettings(result.settings);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save brand settings');
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [holderId],
  );

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  return { settings, isLoading, isSaving, error, loadSettings, saveSettings };
}
